import { useEffect, type RefObject } from 'react';

/**
 * Two-finger pinch zoom on a chart container (iPhone / touch — no wheel).
 * `onZoom(factor, centerClientX)` — factor > 1 = zoom in.
 */
export function usePinchZoom(
  ref: RefObject<HTMLElement | null>,
  onZoom: (factor: number, centerClientX: number) => void,
  enabled = true,
): void {
  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled) return;

    let lastDist = 0;

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        lastDist = Math.hypot(dx, dy);
      } else {
        lastDist = 0;
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length !== 2) {
        lastDist = 0;
        return;
      }
      e.preventDefault();
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.hypot(dx, dy);
      const cx = (e.touches[0].clientX + e.touches[1].clientX) / 2;
      if (lastDist > 0) {
        const factor = dist / lastDist;
        if (Math.abs(factor - 1) > 0.015) onZoom(factor, cx);
      }
      lastDist = dist;
    };

    const reset = () => {
      lastDist = 0;
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', reset);
    el.addEventListener('touchcancel', reset);
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', reset);
      el.removeEventListener('touchcancel', reset);
    };
  }, [ref, onZoom, enabled]);
}
